import { Aviso } from './ui';
import { tokensDesconhecidos, type UsoComVariaveis } from '../servicos/modelosDeContrato';

/**
 * O painel de variáveis de um modelo de contrato (D-020), ao lado do editor:
 * as do uso, em grupos, cada uma com o que ela é. Clicar insere no cursor.
 *
 * É o mesmo na aba Contratos e no cadastro de cada empresa — nos dois, o que
 * estiver entre colchetes e não for variável do uso some do PDF (RN-043).
 */
export function PainelDeVariaveis(props: {
  readonly uso: UsoComVariaveis;
  readonly aoInserir: (token: string) => void;
}): JSX.Element {
  return (
    <aside className="flex flex-col gap-2 lg:sticky lg:top-20 lg:self-start">
      <h3 className="text-sm font-semibold text-conteudo-primario">Variáveis deste uso</h3>
      <p className="text-xs text-conteudo-secundario">
        Clique para inserir onde está o cursor. Ao gerar o PDF, cada uma vira o dado da negociação.
      </p>
      {/*
        O mesmo "Inserir variável…" da barra do editor, aqui em cima da lista,
        separado pelos grupos — para escolher pelo nome sem rolar (29/10/2026).
      */}
      <select
        aria-label="Inserir variável"
        value=""
        onChange={(e) => {
          if (e.target.value !== '') props.aoInserir(e.target.value);
        }}
        className="h-9 w-full rounded-card border border-acento-ciano/50 bg-superficie-base px-2 text-sm text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
      >
        <option value="">Inserir variável…</option>
        {props.uso.grupos.map((g) => (
          <optgroup key={g.titulo} label={g.titulo}>
            {g.variaveis.map((v) => (
              <option key={v.token} value={v.token}>
                {v.descricao}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pr-1 lg:max-h-[calc(100vh-15rem)]">
        {props.uso.grupos.map((g) => (
          <div key={g.titulo} className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-conteudo-sutil">
              {g.titulo}
            </span>
            {g.variaveis.map((v) => (
              <button
                key={v.token}
                type="button"
                title={`Exemplo: ${v.exemplo}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => props.aoInserir(v.token)}
                className="flex w-full flex-col rounded-card border border-borda px-2.5 py-1.5 text-left transition-colors hover:border-acento-ciano/60 hover:bg-superficie-vidro"
              >
                <span className="text-sm text-conteudo-primario">{v.descricao}</span>
                <span className="font-mono text-xs text-acento-ciano">[{v.token}]</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </aside>
  );
}

/** O aviso das variáveis escritas que o uso não conhece — somem do PDF. */
export function AvisoDeVariaveisDesconhecidas(props: {
  readonly conteudo: string;
  readonly uso: UsoComVariaveis;
  /** D-022 — abre a janela que cria as variáveis novas. */
  readonly aoCriar?: () => void;
}): JSX.Element | null {
  const desconhecidos = tokensDesconhecidos(props.conteudo, props.uso);
  if (desconhecidos.length === 0) return null;
  const uma = desconhecidos.length === 1;
  return (
    <Aviso tom="atencao" titulo="Variáveis que não existem neste uso">
      {desconhecidos.map((t) => `[${t}]`).join(', ')} não {uma ? 'é variável' : 'são variáveis'} de
      “{props.uso.rotulo}” e {uma ? 'some' : 'somem'} do PDF.{' '}
      {props.aoCriar === undefined ? (
        'Troque pelas da lista ao lado.'
      ) : (
        <>
          Troque pelas da lista ao lado, ou{' '}
          <button
            type="button"
            onClick={props.aoCriar}
            className="font-medium underline underline-offset-2"
          >
            {uma ? 'crie esta variável' : 'crie estas variáveis'}
          </button>
          .
        </>
      )}
    </Aviso>
  );
}
